import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ReactNode, useState } from "react";

export type FormSectionProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  children?: ReactNode;
  className?: string;
  /** Extra content rendered in the header row (e.g. actions). */
  headerEnd?: ReactNode;
};

export function FormSection({
  title,
  subtitle,
  icon,
  collapsible = false,
  defaultExpanded = true,
  children,
  className,
  headerEnd,
}: FormSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isOpen = collapsible ? expanded : true;

  const header = (
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      spacing={1.5}
      className="intake-section__header"
      sx={{
        cursor: collapsible ? "pointer" : "default",
        userSelect: collapsible ? "none" : "auto",
      }}
      onClick={
        collapsible
          ? () => setExpanded((prev) => !prev)
          : undefined
      }
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ minWidth: 0 }}>
        {icon != null && (
          <Box
            className="intake-section__icon"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              flexShrink: 0,
              borderRadius: "8px",
              backgroundColor: "rgba(237, 99, 58, 0.1)",
              color: "primary.main",
              "& .MuiSvgIcon-root": { fontSize: "1.05rem" },
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h3"
            className="intake-section__title"
            sx={{
              m: 0,
              fontSize: "0.9375rem",
              fontWeight: 600,
              letterSpacing: "-0.01em",
              lineHeight: 1.3,
              color: "text.primary",
            }}
          >
            {title}
          </Typography>
          {subtitle != null && (
            <Typography
              component="p"
              className="intake-section__subtitle"
              sx={{
                m: 0,
                mt: 0.25,
                fontSize: "0.75rem",
                lineHeight: 1.35,
                color: "text.secondary",
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
        {headerEnd}
        {collapsible && (
          <IconButton
            size="small"
            aria-label={isOpen ? "Collapse section" : "Expand section"}
            aria-expanded={isOpen}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
            sx={{
              color: "text.secondary",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        )}
      </Stack>
    </Stack>
  );

  return (
    <Box
      className={["intake-section", className].filter(Boolean).join(" ")}
      sx={{
        backgroundColor: "#FFFFFF",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "12px",
        boxShadow: "0 1px 2px rgba(27, 30, 51, 0.04)",
        p: { xs: 2, md: 2.75 },
      }}
    >
      {header}
      <Collapse in={isOpen} timeout="auto" unmountOnExit={false}>
        <Box className="intake-section__body" sx={{ mt: children != null ? 2 : 0 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

export default FormSection;
